<script setup lang="ts">
import { RULES, type PlayerState } from "../../domain/rules.js";

/**
 * Leaderboard, PRD 6.3.
 *
 * Row anatomy, left to right: rank, name, streak or leader badge, three strike
 * dots, total, distance remaining. Behind the row sits a progress fill sized to
 * the player's share of the target.
 */

const props = defineProps<{
  standings: PlayerState[];
  names: Record<string, string>;
  current: string | null;
  finished: boolean;
}>();

const pct = (total: number): number =>
  Math.max(0, Math.min(100, (total / RULES.target) * 100));

const remaining = (total: number): number => Math.max(0, RULES.target - total);
</script>

<template>
  <ol class="board">
    <li
      v-for="(p, i) in props.standings"
      :key="p.playerId"
      class="row"
      :class="{ 'row--current': p.playerId === props.current && !props.finished }"
    >
      <!-- Decorative: the numbers beside it already carry the information. -->
      <div
        class="fill"
        :class="{ 'fill--leader': i === 0 }"
        :style="{ width: pct(p.total) + '%' }"
        aria-hidden="true"
      />
      <div class="content">
        <span class="rank num">{{ i + 1 }}</span>
        <span class="name">{{ props.names[p.playerId] ?? p.playerId }}</span>

        <span
          v-if="p.total >= RULES.target"
          class="badge"
          title="Im Ziel"
        >🏁</span>
        <span
          v-else-if="i === 0 && p.total > 0"
          class="badge"
          title="Führung"
        >👑</span>
        <span
          v-if="p.streak >= 3"
          class="badge streak"
          :title="`${p.streak} in Folge`"
        >
          🔥<span class="num">{{ p.streak }}</span>
        </span>

        <span
          class="dots"
          :aria-label="`${p.strikes} von 3 Strichen`"
        >
          <i
            v-for="n in 3"
            :key="n"
            class="dot"
            :class="{ 'dot--on': n <= p.strikes }"
          />
        </span>

        <span class="total num">{{ p.total.toLocaleString("de-DE") }}</span>
        <span class="left num">{{ remaining(p.total).toLocaleString("de-DE") }}</span>
      </div>
    </li>
  </ol>
</template>

<style scoped>
.board {
  list-style: none;
  margin: 0;
  padding: 0 12px;
  /* PRD 6.2: at most 31 dvh, scrolling internally only when it overflows.
     `1 1 auto` with min-height 0 lets it give way on short screens, where the
     keypad's floor needs the room more than the board does.

     37dvh rather than the 31dvh in 6.2: at 31dvh only four players fitted on a
     667px phone, and seeing the standings without scrolling matters more to
     the people at the table than matching the number. The space comes off the
     keypad, which 6.2 sized for the thumb rather than for content. */
  flex: 1 1 auto;
  min-height: 0;
  max-height: 37dvh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.row {
  position: relative;
  overflow: hidden;
  margin-bottom: 5px;
  border-radius: var(--radius-card);
  background: var(--card);
  isolation: isolate;
}

.row--current::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: var(--accent);
  z-index: 2;
}

.row--current .name {
  font-weight: 700;
}

.fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--accent-tint);
  transition: width 0.35s ease;
  z-index: 0;
}

.fill--leader {
  background: linear-gradient(90deg, var(--accent-tint), color-mix(in srgb, var(--accent) 35%, transparent));
}

.content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  /* Slimmer rows buy another player on screen. These are not touch targets --
     nothing in a row is tappable -- so the 44px minimum does not apply. */
  padding: 5px 12px;
  min-height: 36px;
}

.rank {
  width: 1.2em;
  color: var(--text-3);
  font-size: 13px;
}

.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
}

.badge {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.dots {
  display: inline-flex;
  gap: 3px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--separator);
}

.dot--on {
  background: var(--danger);
}

.total {
  font-size: 16px;
  font-weight: 600;
  min-width: 3.6em;
  text-align: right;
}

.left {
  font-size: 12px;
  color: var(--text-3);
  min-width: 3.4em;
  text-align: right;
}
</style>
